import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Drawer, Flex, Table, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  exportOperationsRowsCsv,
  fetchOperationsRows,
  type OperationsMetric,
  type OperationsRow,
  type OperationsRowKind,
} from './api';

const { Text } = Typography;

/** Kinds whose second line is a code (a lead's source, an invoice's source, a stage's status), not a plate or chassis. */
const CODED_SUBTITLE: ReadonlySet<OperationsRowKind> = new Set(['lead', 'invoice', 'registration', 'handover']);

/** Where a row opens. A parts request and a reminder have no page of their own; their queue does. */
const SCREEN: Record<OperationsRowKind, (uid: string) => string> = {
  lead: (uid) => `/leads/${uid}`,
  quotation: (uid) => `/quotations/${uid}`,
  booking: (uid) => `/bookings/${uid}`,
  delivery: (uid) => `/deliveries/${uid}`,
  registration: (uid) => `/registration/${uid}`,
  job: (uid) => `/workshop/job-cards/${uid}`,
  'parts-request': () => '/warehouse',
  invoice: (uid) => `/pos/invoices/${uid}`,
  handover: () => '/accounts',
  case: (uid) => `/cases/${uid}`,
  reminder: () => '/reminders',
};

/** `WAITING_PARTS` → `Waiting parts`: readable without a label per status per module. */
export function humanise(code: string): string {
  const words = code.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The rows behind one Operations figure, newest first; each opens its own screen. */
export function OperationsRowsDrawer({
  metric,
  branch,
  title,
  onClose,
}: {
  metric: OperationsMetric | null;
  branch: string | null;
  title: string;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  // Kept through the closing animation, so the drawer does not blank as it slides away.
  const [shown, setShown] = useState<{ metric: OperationsMetric; title: string } | null>(null);
  if (metric !== null && (shown?.metric !== metric || shown.title !== title)) setShown({ metric, title });
  const current = metric ?? shown?.metric ?? null;

  const rows = useQuery({
    queryKey: ['analytics', 'operations', 'rows', current, branch],
    queryFn: () => (current === null ? Promise.reject(new Error('no figure')) : fetchOperationsRows(current, branch)),
    enabled: current !== null,
  });

  const download = async (): Promise<void> => {
    if (current === null) return;
    try {
      const csv = await exportOperationsRowsCsv(current, branch);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `operations-${current.toLowerCase()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t('insights.operations.downloadFailed'));
    }
  };

  const kind = rows.data?.kind;

  return (
    <Drawer
      open={metric !== null}
      title={metric === null ? shown?.title : title}
      onClose={onClose}
      afterOpenChange={(open) => {
        if (!open) setShown(null);
      }}
      width={860}
      className="nks-action-drawer"
      extra={
        <Button icon={<DownloadOutlined />} onClick={() => void download()}>
          {t('insights.lines.downloadCsv')}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {rows.error !== null && (
          <Text type="danger">{rows.error instanceof ApiError ? rows.error.message : t('insights.loadFailed')}</Text>
        )}
        {rows.data?.truncated === true && <Text type="secondary">{t('insights.operations.truncated')}</Text>}
        <Table<OperationsRow>
          rowKey="uid"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={rows.isPending && current !== null}
          locale={{ emptyText: t('insights.operations.noRows') }}
          dataSource={rows.data?.rows ?? []}
          onRow={(r) => {
            const go = (): void => {
              if (kind !== undefined) void navigate(SCREEN[kind](r.uid));
            };
            return {
              style: { cursor: 'pointer' },
              tabIndex: 0,
              role: 'link',
              onClick: go,
              onKeyDown: (e) => {
                if (e.key === 'Enter') go();
              },
            };
          }}
          columns={[
            {
              title: t('insights.operations.ref'),
              key: 'ref',
              render: (_: unknown, r) => (
                <Flex vertical>
                  <Text className="tabular">{r.ref}</Text>
                  {r.subtitle !== null && (
                    <Text type="secondary">
                      {kind !== undefined && CODED_SUBTITLE.has(kind) ? humanise(r.subtitle) : r.subtitle}
                    </Text>
                  )}
                </Flex>
              ),
            },
            { title: t('insights.operations.who'), dataIndex: 'title', render: (v: string | null) => v ?? '—' },
            {
              title: t('insights.operations.when'),
              dataIndex: 'at',
              render: (v: string | null) => <span className="tabular">{formatDateTime(v)}</span>,
            },
            {
              title: t('insights.operations.status'),
              dataIndex: 'status',
              render: (v: string | null) => (v === null ? '—' : humanise(v)),
            },
            {
              title: t('insights.operations.amount'),
              dataIndex: 'amount',
              align: 'right',
              render: (v: string | null) => (v === null ? '' : formatMoney(v)),
            },
          ]}
        />
      </Flex>
    </Drawer>
  );
}
